import merge from 'lodash/merge'
import omit from 'lodash/omit'
import some from 'lodash/some'
import { ApiProduct } from '@vend/types'
import { add, round, vn } from 'vend-number'
import { trackError } from '@vend/utilities'
import { reduce } from 'lodash'
import { LineItem } from '../models/line-item'
import {
  getInventory,
  getInventoryLegacy,
} from '../components/product/products-resource/get-inventory'
import { CustomFieldValue } from '../models/custom-fields'
/**
 * Return true if the given product is Gift Card
 *
 * @param  product {ApiProduct}
 *         The product to inspect
 *
 * @return True if the given product is Gift Card, false otherwise
 */
export const isGiftCard = (product: ApiProduct) => {
  return Boolean(
    product &&
      some(product.attributes, {
        name: 'vend.is_gift_card',
        value: '1',
      })
  )
}

export const getTotalProductQuantity = (
  lineItems: LineItem[],
  productId: string
) => {
  return lineItems.reduce((total, lineItem) => {
    return lineItem.product.id === productId
      ? add(total, lineItem.quantity)
      : total
  }, 0)
}

export interface LineItemQuantities {
  [key: string]: number
}

export const getTotalProductQuantities = (lineItems: LineItem[]) => {
  const quantities: LineItemQuantities = {}
  const productIds = lineItems.map(l => l.product.id)
  const uniqProductIds = [...new Set(productIds)]

  uniqProductIds.forEach(id => {
    quantities[id] = getTotalProductQuantity(lineItems, id)
  })

  return quantities
}

export const getOnHandProductQuantities = (
  lineItems: LineItem[],
  outletId: string,
  use202604LevelsApi: boolean
) => {
  return new Promise<LineItemQuantities>((resolve, reject) => {
    const quantities: LineItemQuantities = {}

    const productsById = new Map<string, ApiProduct>()
    lineItems.forEach(({ product }) => {
      if (!productsById.has(product.id)) {
        productsById.set(product.id, product)
      }
    })

    if (!use202604LevelsApi) {
      return getInventoryLegacy([...productsById.keys()], {
        include_variants: false,
        location_ids: [outletId],
      })
        .then(inventoryData => {
          inventoryData.forEach(i => {
            const level = productsById.get(i.product_id)?.is_composite
              ? i.levels.producible
              : i.levels.on_hand
            quantities[i.product_id] = level
          })
          resolve(quantities)
        })
        .catch(error => {
          trackError('Failed to retrieve on hand inventory levels', {
            data: { error },
          })
          reject(error)
        })
    }

    return getInventory([...productsById.keys()], {
      include_variants: false,
      location_ids: [outletId],
    })
      .then(inventoryData => {
        inventoryData.forEach(i => {
          quantities[i.product_id] = i.current_inventory_level
        })
        resolve(quantities)
      })
      .catch(error => {
        trackError('Failed to retrieve on hand inventory levels', {
          data: { error },
        })
        reject(error)
      })
  })
}

export function hasInsuficientInventory(
  inventoryData: Record<string, number>,
  productId: string,
  productQuantity: string | number
): boolean {
  // Convert productQuantity to a number
  const requiredQuantity = Number(productQuantity)

  if (
    inventoryData.size === 0 ||
    !(productId in inventoryData) ||
    isNaN(requiredQuantity)
  ) {
    return false
  }

  return inventoryData[productId] < requiredQuantity
}

/**
 * When discount is greater than 0, price is discounted. When discount is 0, it is not.
 *
 * @return True if base price has been discounted.
 */
export const isPriceDiscounted = (lineItem: LineItem) => {
  return vn(lineItem.displayDiscountPercent).gt(0)
}

/**
 * Determine if the line item has variant options
 *
 * @return If the line item has variants
 */
export const hasVariants = (lineItem: LineItem) => {
  return Boolean(
    lineItem.product &&
      lineItem.product.variant_options &&
      lineItem.product.variant_options.length
  )
}

/**
 * Returns a string representation of the variant options for a line item
 *
 * @return The string representation of the variant options
 */
export const getVariantsDisplayNames = (lineItem: LineItem) => {
  const variantsDisplayNames = lineItem.product.variant_options.map(variant => {
    return variant.value
  })

  return variantsDisplayNames.join(' / ')
}

/**
 * Determine if the product is deleted
 * Note: Here it is assumed that if the product could not be found that the product is deleted
 *
 * @return If the product is deleted
 */
export const isDeletedProduct = (lineItem: LineItem) => {
  return Boolean(lineItem.product && lineItem.product.deleted_at)
}

/**
 * Get the tax code of a lineItem
 *
 * @return Tax Code
 */
export const getTaxCode = (lineItem: LineItem) => {
  if (
    lineItem.product &&
    lineItem.product.attributes &&
    lineItem.product.attributes.length
  ) {
    const taxCode = lineItem.product.attributes.find(
      attribute => attribute.name === 'tax_code'
    )

    return taxCode ? taxCode.value : ''
  }

  return ''
}

/**
 * Returns a JSON representation of this line item suitable for storing in IndexedDB. The referenced
 * product and tax are replaced with lighter IDs-only equivalent.
 *
 * @return {Object} JSON representation of this line item
 */
export const lineItemToJSON = (lineItem: LineItem) => {
  const data = { ...lineItem }
  const newData = merge(omit(data, 'product', 'taxInfo'), {
    product_id: lineItem.product.id,
    tax_id: lineItem.taxInfo && lineItem.taxInfo.id,
  })

  return newData
}

/**
 * Return true if the sale contains any Gift Card line item
 *
 * @return {Boolean} True if the sale contains any Gift Card line item
 */
export const containsGiftCard = (lineItems: LineItem[]) => {
  return lineItems.some(lineItem => isGiftCard(lineItem.product))
}

/**
 * Find the total number of items, as some line items have more than one quantity.
 *
 * @return The total number of items.
 */
export const getBasketSize = (
  lineItems: ReadonlyArray<Readonly<LineItem>>
): number => {
  let basketSize: number | string = reduce(
    lineItems,
    (total, lineItem) =>
      add(
        // TODO: Unsure how lineItem.quantity is NaN but it does happen and this is throwing errors to Raygun when
        // it does. Rather ignore that line item's quantity in this situation.
        isFinite(Number(lineItem.quantity ?? 0)) ? lineItem.quantity : 0,
        total
      ),
    0
  )
  basketSize = round(basketSize, 3)
  return vn(basketSize).toNumber()
}

/**
 * Checks if the line item can be consolidated with another line item.
 *
 * @param lineItem {LineItem}
 * @returns boolean
 */
export const canConsolidateLineItem = (lineItem: LineItem): boolean => {
  const quantity = Number(lineItem.quantity)
  const hasNegativeQuantity = quantity < 0
  const isReturn = lineItem.isReturn
  const isConfirmed = lineItem.confirmed
  const isGiftCard = lineItem.giftCardNumber

  return !hasNegativeQuantity && !isReturn && !isConfirmed && !isGiftCard
}

const SERIAL_NUMBER_FIELD = 'vend/serial-number'
const SERIAL_NUMBERS_FIELD = 'vend/serial-numbers'
const SKIPPED_SERIAL_NUMBER_VALUE = '\u2014' // em dash, set by @vend/business-components when user skips

/**
 * Removes skipped serial number custom fields from a line item.
 * Clears `vend/serial-number` if its value is the skip placeholder, and clears
 * the entire `vend/serial-numbers` field if any element in its array is the
 * skip placeholder. The fact that we are clearing out the entire vend/serial-numbers
 * array if ONE contains the skipped character is intentional.
 * This is not a common case and the worst case scenario is that the user has to
 * re-enter the serial numbers.
 */
export function clearSkippedSerialNumbers(
  customFields: CustomFieldValue[]
): CustomFieldValue[] {
  return customFields.filter(cf => {
    if (
      cf.name === SERIAL_NUMBER_FIELD &&
      cf.value === SKIPPED_SERIAL_NUMBER_VALUE
    ) {
      return false
    }
    if (
      cf.name === SERIAL_NUMBERS_FIELD &&
      Array.isArray(cf.value) &&
      cf.value.some(v => v === SKIPPED_SERIAL_NUMBER_VALUE)
    ) {
      return false
    }
    return true
  })
}

/**
 * Calculate the quantities for splitting a line item.
 */
export const calculateSplitQuantities = (
  lineItem: LineItem,
  splitQuantity: number
) => {
  // updatedLineItemQuantity represents the updated quantity of the line item we are splitting off from
  // newSplitLineItemQuantity represents the quantity of the new line item which will have the same amount as the split quantity (either positive or negative if a return)
  return vn(lineItem.quantity).greaterThan(0)
    ? {
        updatedLineItemQuantity: vn(lineItem.quantity)
          .sub(splitQuantity)
          .toNumber(),
        newSplitLineItemQuantity: splitQuantity,
      }
    : {
        updatedLineItemQuantity: vn(lineItem.quantity)
          .add(splitQuantity)
          .toNumber(),
        newSplitLineItemQuantity: -splitQuantity,
      }
}
